import { useCallback, useState } from "react";
import { HostedFieldsTokenizePayload, ApplePayPayload } from "braintree-web";
import { ConfirmationToken } from "@stripe/stripe-js";
import { AuthorizationResponse } from "paypal-checkout-components";
import {
  AllowedPaymentMethod,
  AvailablePaymentMethodsFragment,
  CreditCardBillingAddress,
  SavePaymentInput,
  useSetupTransactionMutation,
} from "v3/services/graphql/generated";

export interface HandleSelectSavedCreditCard {
  billingAddress: CreditCardBillingAddress;
  token: string;
}

export interface HandleSelectSavedPayment {
  token: string;
}

export interface HandleSelectSavedAllocatedCredit {
  id: string;
}

export interface HandleSelectNewPayment {
  paymentNonce: string;
}

export interface PaymentContextInterface {
  availablePaymentMethods: AvailablePaymentMethodsFragment | null;
  clearPaymentDataOnEdit: () => void;
  handleSelectApplePay: (props: HandleSelectNewPayment) => void;
  handleSelectInvoice: () => void;
  handleSelectSavedAllocatedCredit: (
    props: HandleSelectSavedAllocatedCredit
  ) => void;
  handleSelectNewBlik: (props: HandleSelectNewPayment) => void;
  handleSelectNewCreditCard: (props: HandleSelectNewPayment) => void;
  handleSelectNewP24: (props: HandleSelectNewPayment) => void;
  handleSelectNewPayPal: (props: HandleSelectNewPayment) => void;
  handleSelectSavedCreditCard: (props: HandleSelectSavedCreditCard) => void;
  handleSelectSavedPayPal: (props: HandleSelectSavedPayment) => void;
  newApplePayResponse: ApplePayPayload | null;
  newCreditCardBraintreeResponse: HostedFieldsTokenizePayload | null;
  newPayPalResponse: AuthorizationResponse | null;
  paymentNonce: string | null;
  paymentType: AllowedPaymentMethod | null;
  saveCardForLater: boolean;
  savePaymentInput: SavePaymentInput | null;
  setAvailablePaymentMethods: (
    methods: AvailablePaymentMethodsFragment
  ) => void;
  setNewApplePayResponse: (data: ApplePayPayload) => void;
  setNewCreditCardBraintreeResponse: (
    payload: HostedFieldsTokenizePayload
  ) => void;
  generatePaymentIntentSecret: (checkoutSessionId: string) => Promise<void>;
  isBillingEqualShipping: boolean | null;
  paymentIntentSecret: string | null;
  setIsBillingEqualShipping: (val: boolean) => void;
  setNewPayPalResponse: (data: AuthorizationResponse) => void;
  setPaymentNonce: (paymentNonce: string | null) => void;
  setPaymentIntentSecret: (val: string | null) => void;
  setPaymentType: (value: AllowedPaymentMethod) => void;
  setSaveCardForLater: (saveCardForLater: boolean) => void;
  setStripeTokenDetails: (tokenDetails: ConfirmationToken) => void;
  stripeTokenDetails: ConfirmationToken | null;
}

export function usePaymentProvider(): {
  paymentContextValue: PaymentContextInterface;
} {
  const [setupTransaction] = useSetupTransactionMutation();

  const [availablePaymentMethods, setAvailablePaymentMethods] =
    useState<AvailablePaymentMethodsFragment | null>(null);
  const [newCreditCardBraintreeResponse, setNewCreditCardBraintreeResponse] =
    useState<HostedFieldsTokenizePayload | null>(null);
  const [newPayPalResponse, setNewPayPalResponse] =
    useState<AuthorizationResponse | null>(null);
  const [newApplePayResponse, setNewApplePayResponse] =
    useState<ApplePayPayload | null>(null);
  const [savePaymentInput, setSavePaymentInput] =
    useState<SavePaymentInput | null>(null);
  const [paymentNonce, setPaymentNonce] = useState<string | null>(null);
  const [paymentType, setPaymentType] = useState<AllowedPaymentMethod | null>(
    null
  );
  const [isBillingEqualShipping, setIsBillingEqualShipping] = useState<
    boolean | null
  >(null);
  const [stripeTokenDetails, setStripeTokenDetails] =
    useState<ConfirmationToken | null>(null);
  const [paymentIntentSecret, setPaymentIntentSecret] = useState<string | null>(
    null
  );
  const [saveCardForLater, setSaveCardForLater] = useState(false);

  const handleSelectSavedCreditCard = useCallback(
    ({ billingAddress, token }: HandleSelectSavedCreditCard) => {
      const { __typename, company, ...rest } = billingAddress;
      setIsBillingEqualShipping(false);
      setPaymentType(AllowedPaymentMethod.CreditCard);
      setSavePaymentInput({
        savedCreditCard: {
          billingAddress: rest,
          token,
        },
      });
      setPaymentNonce(null);
    },
    []
  );

  const generatePaymentIntentSecret = useCallback(
    async (id: string) => {
      const { data: setupTransactionData } = await setupTransaction({
        variables: {
          id,
        },
      });
      setPaymentIntentSecret(
        setupTransactionData?.setupTransaction?.payment?.token || null
      );
    },
    [setupTransaction]
  );

  const handleSelectNewCreditCard = useCallback(
    ({ paymentNonce }: HandleSelectNewPayment) => {
      // we don't setSavePaymentInput here because we don't get the values
      // until the form is submitted and we call savePayment directly from submit
      setSavePaymentInput({});
      setPaymentNonce(paymentNonce);
      setPaymentType(AllowedPaymentMethod.CreditCard);
    },
    []
  );

  const handleSelectNewP24 = useCallback(
    ({ paymentNonce }: HandleSelectNewPayment) => {
      // we don't setSavePaymentInput here because we don't get the values
      // until the form is submitted and we call savePayment directly from submit
      setSavePaymentInput({});
      setPaymentNonce(paymentNonce);
      setPaymentType(AllowedPaymentMethod.P24);
    },
    []
  );

  const handleSelectNewBlik = useCallback(
    ({ paymentNonce }: HandleSelectNewPayment) => {
      // we don't setSavePaymentInput here because we don't get the values
      // until the form is submitted and we call savePayment directly from submit
      setSavePaymentInput({});
      setPaymentNonce(paymentNonce);
      setPaymentType(AllowedPaymentMethod.Blik);
    },
    []
  );

  const handleSelectSavedPayPal = useCallback(
    ({ token }: HandleSelectSavedPayment) => {
      setSavePaymentInput({
        paypal: { token },
      });
      setPaymentNonce(null);
      setPaymentType(AllowedPaymentMethod.Paypal);
    },
    []
  );

  const handleSelectNewPayPal = useCallback(
    ({ paymentNonce }: HandleSelectNewPayment) => {
      setSavePaymentInput({
        paypal: {},
      });
      setPaymentNonce(paymentNonce);
      setPaymentType(AllowedPaymentMethod.Paypal);
    },
    []
  );

  const handleSelectApplePay = useCallback(
    ({ paymentNonce }: HandleSelectNewPayment) => {
      setSavePaymentInput({
        applePay: {},
      });
      setPaymentNonce(paymentNonce);
      setPaymentType(AllowedPaymentMethod.ApplePay);
    },
    []
  );

  const handleSelectInvoice = useCallback(() => {
    setSavePaymentInput({ invoice: { unused: false } });
    setPaymentNonce(null);
    setPaymentType(AllowedPaymentMethod.Invoice);
  }, []);

  const handleSelectSavedAllocatedCredit = useCallback(
    ({ id }: HandleSelectSavedAllocatedCredit) => {
      setSavePaymentInput({ allocatedCredit: { id } });
      setPaymentNonce(null);
      setPaymentType(AllowedPaymentMethod.AllocatedCredit);
    },
    []
  );

  const clearPaymentDataOnEdit = useCallback(() => {
    setNewApplePayResponse(null);
    setNewPayPalResponse(null);
    setSavePaymentInput(null);
    setPaymentNonce(null);
    setPaymentType(null);
  }, []);

  const paymentContextValue: PaymentContextInterface = {
    availablePaymentMethods,
    clearPaymentDataOnEdit,
    generatePaymentIntentSecret,
    handleSelectApplePay,
    handleSelectInvoice,
    handleSelectSavedAllocatedCredit,
    handleSelectNewBlik,
    handleSelectNewCreditCard,
    handleSelectNewP24,
    handleSelectNewPayPal,
    handleSelectSavedCreditCard,
    handleSelectSavedPayPal,
    isBillingEqualShipping,
    newApplePayResponse,
    newCreditCardBraintreeResponse,
    newPayPalResponse,
    paymentIntentSecret,
    paymentNonce,
    paymentType,
    saveCardForLater,
    savePaymentInput,
    setAvailablePaymentMethods,
    setIsBillingEqualShipping,
    setPaymentIntentSecret,
    setNewApplePayResponse,
    setNewCreditCardBraintreeResponse,
    setNewPayPalResponse,
    setPaymentNonce,
    setPaymentType,
    setSaveCardForLater,
    setStripeTokenDetails,
    stripeTokenDetails,
  };

  return { paymentContextValue };
}
